export const ACCORDION = {
  init() {
    Accordion.initAll('[data-accordion]')
  },
}

class Accordion {
  static initAll(selector = '[data-accordion]') {
    const roots = document.querySelectorAll(selector)

    roots.forEach(root => {
      if (root.__accordionInstance) return
      root.__accordionInstance = true

      const ins = new Accordion(root)
      ins.init()
    })
  }

  constructor(root) {
    this.root = root
    this.trigger = root.querySelector('[data-accordion-trigger]')
    this.panel = root.querySelector('[data-accordion-panel]')

    this.duration = this._getDuration()
    this._isAnimating = false
    this._tid = 0

    this.onClick = this.onClick.bind(this)
    this.onKeydown = this.onKeydown.bind(this)
  }

  init() {
    if (!this.trigger || !this.panel) return

    if (!this.trigger.hasAttribute('tabindex')) this.trigger.setAttribute('tabindex', '0')
    this.trigger.setAttribute('role', 'button')

    if (!this.panel.id) {
      this.panel.id = `acc-panel-${Math.random().toString(16).slice(2)}`
    }
    this.trigger.setAttribute('aria-controls', this.panel.id)

    const isOpen = this.root.getAttribute('data-accordion-open') === 'true'
    this._applyState(isOpen, { skipAnim: true })

    this.trigger.addEventListener('click', this.onClick)
    this.trigger.addEventListener('keydown', this.onKeydown)
  }

  destroy() {
    if (!this.trigger || !this.panel) return
    this.trigger.removeEventListener('click', this.onClick)
    this.trigger.removeEventListener('keydown', this.onKeydown)
    this._clearTimer()
  }

  onClick(e) {
    e.preventDefault()
    this.toggle()
  }

  onKeydown(e) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      this.toggle()
    }
  }

  toggle() {
    if (this._isAnimating) return
    const nextOpen = !this.root.classList.contains('is-open')
    this._applyState(nextOpen, { skipAnim: false })
  }

  open() {
    if (this._isAnimating) return
    this._applyState(true, { skipAnim: false })
  }

  close() {
    if (this._isAnimating) return
    this._applyState(false, { skipAnim: false })
  }

  _applyState(open, { skipAnim }) {
    this._clearTimer()

    // reduced motion
    const reduced =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const anim = !skipAnim && !reduced && this.duration > 0

    this.root.classList.toggle('is-open', open)
    this.trigger.setAttribute('aria-expanded', open ? 'true' : 'false')

    if (!anim) {
      if (open) {
        this.panel.hidden = false
        this.panel.style.height = ''
      } else {
        this.panel.style.height = ''
        this.panel.hidden = true
      }
      return
    }

    this._isAnimating = true
    this.panel.hidden = false

    if (open) {
      this.panel.style.height = '0px'
      this._reflow(this.panel)

      const target = this.panel.scrollHeight
      this.panel.style.transition = `height ${this.duration}ms ease`
      this.panel.style.height = `${target}px`

      this._tid = window.setTimeout(() => {
        this.panel.style.transition = ''
        this.panel.style.height = ''
        this._isAnimating = false
      }, this.duration + 30)
    } else {
      const start = this.panel.getBoundingClientRect().height
      this.panel.style.height = `${start}px`
      this._reflow(this.panel)

      this.panel.style.transition = `height ${this.duration}ms ease`
      this.panel.style.height = '0px'

      this._tid = window.setTimeout(() => {
        this.panel.style.transition = ''
        this.panel.style.height = ''
        this.panel.hidden = true
        this._isAnimating = false
      }, this.duration + 30)
    }
  }

  _getDuration() {
    const v = this.root.getAttribute('data-accordion-duration')
    const n = parseInt(v, 10)
    return Number.isFinite(n) ? n : 300
  }

  _clearTimer() {
    if (this._tid) {
      clearTimeout(this._tid)
      this._tid = 0
    }
    if (this.panel) this.panel.style.transition = ''
    this._isAnimating = false
  }

  _reflow(el) {
    const _ = el.offsetHeight // eslint-disable-line no-unused-vars
    return _
  }
}
